import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { FaDuotoneIconComponent } from '@fortawesome/angular-fontawesome';
import { TooltipDirective } from '../../../../shared/components/ui/tooltip/tooltip.directive';
import { TranslatePipe } from '@ngx-translate/core';
import {
  faCircleCheck,
  faCircleInfo,
  faCircleXmark,
  faTriangleExclamation,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';

export type NoticeVariant = 'info' | 'success' | 'warning' | 'error';

const VARIANT_ICONS = {
  info: faCircleInfo,
  success: faCircleCheck,
  warning: faTriangleExclamation,
  error: faCircleXmark,
} as const;

/**
 * A tinted callout box for a short inline message. `variant` selects the
 * accent — info (accent color), success, warning, error — each keyed to the
 * matching `--*-color` CSS variable, so it stays correct in light and dark
 * themes. The message is projected via `<ng-content>`; set `dismissible` to
 * show a close button that emits `(dismissed)`. It fills its container's width
 * (cap it with `max-width`); below 16rem of content width the icon stacks
 * centered above the text.
 */
@Component({
  selector: 'andor-notice',
  standalone: true,
  imports: [FaDuotoneIconComponent, TooltipDirective, TranslatePipe],
  templateUrl: './notice.html',
  styleUrl: './notice.scss',
  host: {
    class: 'notice',
    '[class.notice-info]': "variant() === 'info'",
    '[class.notice-success]': "variant() === 'success'",
    '[class.notice-warning]': "variant() === 'warning'",
    '[class.notice-error]': "variant() === 'error'",
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Notice {
  variant = input<NoticeVariant>('info');
  dismissible = input(false);
  dismissed = output<void>();

  protected readonly faXmark = faXmark;
  protected readonly icon = computed(() => VARIANT_ICONS[this.variant()]);
}
